<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import type { Component } from 'vue';

import Inquiry from '@/javascripts/utils/inquiry';

import { useMobileFlag } from '@/javascripts/composables/browser';
import { useInquiryValidation } from '@/javascripts/composables/inquiry/validation';

// コンポーネント
import SlideIndicator from './slide_form2023/slide_indicator.vue';
import PharmacistLicenseSlide from './slide_form_license_check/slide1.vue';
import EmploymentTypeSlide from './slide_form2023/slide1.vue';
import WishfulTimesSlide from './slide_form2023/slide2.vue';
import PrefCityPositionTypeSlide from './slide_form2023/slide3.vue';
import NameBirthYearSlide from './slide_form2023/slide4.vue';
import PhoneEmailCommentSlide from './slide_form2023/slide5.vue';

/** 問い合わせオブジェクト */
const inquiry = reactive(new Inquiry);

/** スライドコンポーネントリスト */
const slideComponents: Component[] = [
  PharmacistLicenseSlide,
  EmploymentTypeSlide,
  WishfulTimesSlide,
  PrefCityPositionTypeSlide,
  NameBirthYearSlide,
  PhoneEmailCommentSlide
];

/** 現在のスライド */
const currentSlide = ref(1);
/** 最後のスライド */
const maxSlide = slideComponents.length;
/** 現在のスライドコンポーネント */
const currentSlideComponent = computed(() => slideComponents[currentSlide.value - 1]);
/** トランジション名 */
const transitionName = ref<'next' | 'back'>('next');
/** 問い合わせのバリデーション */
const v$ = useInquiryValidation(inquiry);
/** 遷移中フラグ */
const isTransitioning = ref(false);

/** `next`イベントハンドラー */
const next = () => {
  if (isTransitioning.value) return;

  transitionName.value = 'next';
  currentSlide.value++;
}

/** `back`イベントハンドラー */
const back = () => {
  if (isTransitioning.value) return;

  transitionName.value = 'back';
  currentSlide.value--;
}

/** モバイルかどうか */
const isMobile = useMobileFlag();
/** SPの場合は「登録は1分で完了」の帯の位置にスクロールさせる */
const scrollToFormTopWhenMobile = () => {
  // AGT-1384: PC表示ではスクロールしない（ヘッダー固定のため）
  if(!isMobile.value) return;

  // 「登録は1分で完了」の帯（.form_header）の要素を取得
  const formHeaderElement = document.querySelector('.main_visual_form .form_header');
  if(!formHeaderElement) return;

  // 帯が画面トップに来るようにスクロール
  // これにより、帯の内容も見えて、かつフォーム本体も見切れない
  formHeaderElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
</script>

<template>
  <div class="form_body">
    <SlideIndicator
      :max="maxSlide"
      :current="currentSlide"
    />
    <Transition
      :name="transitionName"
      mode="out-in"
      @leave="scrollToFormTopWhenMobile"
      @before-leave="isTransitioning = true"
      @before-enter="isTransitioning = true"
      @after-enter="isTransitioning = false"
    >
      <KeepAlive>
        <component
          :is="currentSlideComponent"
          :inquiry="inquiry"
          :current-slide-value="currentSlide"
          :v$="v$"
          @next="next"
          @back="back"
        />
      </KeepAlive>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@use '@/stylesheets/common_style/slide_animation.scss';

// フォームボディ
.form_body {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding-top: 22px;
  width: 100%;
  box-shadow: 0 0 13px rgba(61, 61, 61, 0.141);
}

$default-color: #bfbfbf;
$primary-color: #f9982c;
$primary-color-checked: #d9d9e8;
$base-text-color: #006;

// 以下子コンポーネントに適用されるスタイルをdeepセレクターで指定
:deep {
  .heading {
    margin-bottom: 0.3em;
    text-align: center;

    h3 {
      font-size: 1em;
      margin-bottom: 1em;

      strong {
        font-weight: 900;
      }

      .highlighted {
        position: relative;
        text-decoration-line: underline;
        text-decoration-color: #ffe828;
        text-decoration-thickness: 0.4em;
        text-underline-offset: -0.4em;
        text-decoration-skip-ink: none;
      }
    }

    a {
      color: #0033aa;
      display: inline-block;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

  }
    
  // スライドボディ(各スライドコンポーネントに適応される)
  .slide_body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    margin: 1.3em 1.75em 0.75em 1.75em;

    .option_wrap {
      flex-grow: 1;
    }

    // 設問タイトル
    h3.option_title {
      margin: 1em 0;
      font-size: 1.1em;

      &:not(:first-of-type) {
        margin-top: 2.25em;
      }
    }

    .option_title {
      // 必須
      &.required {
        font-weight: bold;
      }

      // 任意アイコン
      &::before {
        display: inline-block;
        content: '任意';
        padding: 4px 6px;
        margin-right: 0.7em;
        background-color: $default-color;
        font-size: 0.9rem;
        color: #fff;
      }

      // 必須アイコン
      &.required::before {
        content: '必須';
        background-color: $primary-color;
      }

      // 注釈
      .annotation {
        margin-left: 4em;
        font-weight: 300;
        font-size: 0.8em;
      }
    }

    // 設問ボタングループ
    .option_group {
      margin: 0.75em 0;
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: 0.75em;

      .long {
        grid-column: 1/ 3;
      }

      &.columns3 {
        grid-template-columns: minmax(4em, auto) 1fr 1fr;

        .long {
          grid-column: 2 / 4;
        }
      }

      // 設問ボタン
      .option_input {

        // ラジオボタン・チェックボックス
        input[type='radio'], input[type='checkbox'] {
          display: none;

          // チェック状態
          &:checked + label {
            border-color: $primary-color;
            background-color: $primary-color-checked;
          }
        }

        input[type='radio']:required + label {
          border: 1px solid $primary-color;
        }

        // 入力フォーム
        label, input, select, textarea {
          appearance: none;
          display: block;
          width: calc(100% - 1.5em);
          padding: 1em 0.75em;
          background-color: #fff;
          border: 1px solid $default-color;
          border-radius: 4px;
          font-weight: 300;
          font-size: 1rem;

          &:required {
            border-color: $primary-color;
          }
        }

        label {
          text-align: center;
          cursor: pointer;
        }

        // 選択リスト
        &:has(select) {
          position: relative;

          // 下矢印アイコン
          &::after {
            display: block;
            content: '';
            border: 1px solid transparent;
            border-top-color: #000;
            border-width: 0.6em 0.4em 0 0.4em;
            position: absolute;
            top: calc(50% - 0.3em);
            right: 0.8em;
            pointer-events: none;
          }
        }

        select {
          width: 100%;
          cursor: pointer;
          background-color: #fff;
          color: #000;

          &:required:valid {
            background-color: $primary-color-checked;
          }
        }

        textarea {
          height: 3em;
        }
      }
    }

    // 設問注釈ボックス
    .option_annotation_box {
      margin: 8px 0;
      font-size: 0.9em;
      text-align: center;

      a {
        color: #0033aa;
        text-decoration: none;
        cursor: pointer;

        &:hover, &:active {
          text-decoration: underline;
        }
      }
    }

    // バリデーションメッセージボックス
    .validation_message_box {
      margin: 0.6em 0;
      font-size: 0.9em;
      color: #ff5722;
    }

    // スライドコントロールグループ
    .slide_control_group {
      margin: 16px 0;
      display: flex;
      justify-content: center;
      align-items: center;

      // 「戻る」リンク
      .back_link {
        display: inline;
        margin-right: 1.5em;
        text-decoration: underline;
        color: #848282;
        cursor: pointer;
      }

      // ボタン
      // ※スタイル定義の大半は`app/assets/stylesheets/inquiry_forms2023/slide_form/_button.sass`に記載
      .form_button {
        width: calc(100% - 6em);
        max-width: 21em;
      }
    }
  }
}
</style>
